import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CookieBanner } from '../../components/CookieBanner';
import { CookieConsentModal } from '../../components/CookieConsentModal';
import { COOKIE_BANNER_AUTO_ACCEPT_TIMEOUT_MS } from '../../constants/cookieConsent';
import { acceptCookieConsent, isCookieConsentAccepted } from '../../utils/cookieConsent';

export const CookieConsentContainer: React.FC<{ appearance?: 'light' | 'warm' }> = ({ appearance }) => {
  const [shouldRender] = useState(() => !isCookieConsentAccepted());
  const [isBannerVisible, setIsBannerVisible] = useState(shouldRender);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const accept = useCallback(() => {
    clearTimer();
    acceptCookieConsent();
    setIsModalOpen(false);
    setIsBannerVisible(false);
  }, [clearTimer]);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(accept, COOKIE_BANNER_AUTO_ACCEPT_TIMEOUT_MS);
  }, [accept, clearTimer]);

  useEffect(() => {
    if (isBannerVisible && !isModalOpen) {
      startTimer();
    }

    return clearTimer;
  }, [isBannerVisible, isModalOpen, startTimer, clearTimer]);

  const handleAgreementClick = useCallback(() => {
    clearTimer();
    setIsModalOpen(true);
  }, [clearTimer]);

  const handleModalCancel = useCallback(() => {
    clearTimer();
    setIsModalOpen(false);
    setIsBannerVisible(false);
  }, [clearTimer]);

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {isBannerVisible && !isModalOpen && (
        <CookieBanner
          appearance={appearance}
          onAgreementClick={handleAgreementClick}
          onClose={accept}
          timerDurationMs={COOKIE_BANNER_AUTO_ACCEPT_TIMEOUT_MS}
        />
      )}

      <CookieConsentModal isOpen={isModalOpen} onAccept={accept} onCancel={handleModalCancel} />
    </>
  );
};

CookieConsentContainer.displayName = 'CookieConsentContainer';
